<script>
  import { browser } from "$app/env";
  import { onDestroy, onMount } from "svelte";
  import { unique_classes } from '#lib/util.js';

  /**
   * @features
   * - No External Dependencies: Works independently without needing libraries like `floating-ui`.
   * - Native Control: Can be controlled via native popover APIs (`popovertargetaction` and `popovertarget`).
   * - Programmatic Control: Manageable programmatically through a `popover` reference, using `showPopover()` or `hidePopover()`.
   * - Dual Modes: Supports 'auto' and 'manual' modes. 'Auto' mode reacts to clicks outside or ESC key to hide the popover, while 'manual' mode does not.
   * - Intelligent Auto Flip: Uses surface-area-based placement optimization with configurable intent bias (default 25%). 
   *   Only flips when alternatives offer significantly better interaction space, respecting developer intent while maximizing usability.
   *   Includes anti-flickering mechanisms: stability bias (15%), flip threshold (10%), and cooldown period (300ms).
   *   Flip logic uses anchor-relative space calculations to prevent false positives from squeezed popover dimensions.
   * - Positioning: Compatible with anchor elements having 'fixed' or 'sticky' positioning.
   * - Clean Anchor: The anchor element is kept outside of the popover to maintain a clean DOM structure.
   * - Animation: Instant open, subtle fade+scale exit animation toward anchor.
   * - Auto-Portal: Automatically appends to document.body to prevent clipping and stacking context issues.
   * 
   * Note: Uses `position: absolute` and converts viewport → page coordinates when positioning.
   * If you switch to `position: fixed`, remove the page-offset conversion in `compute_position()`.
   * 
   * @limitations
   * - Popover is portaled to document.body (required for proper positioning)
   * - CSS custom properties are automatically captured and applied, but won't update reactively if parent changes them
   * - Cannot use parent component CSS selectors for conditional styling (must use props/classes)
   * 
   * @example
   * <Popover 
   *   id="unique_id" 
   *   mode="auto" 
   *   ref={popover_ref} 
   *   placement="bottom-start" 
   *   anchor={element_ref} 
   *   flip={true} 
   *   backdrop={false}
   *   class="normal"
   * >
   *   <!-- Popover content here -->
   * </Popover>
   */

  let {
    /** @type {any} Render children sinppets */
    children,
    /** @type {string} A unique identifier, required for native popover API */
		id, 
    /** @type {'manual'|'auto'} Controls the popover's interaction mode */
		mode = 'auto', 
    // IMPORTANT NOTE ABOUT "MODE":
    // one cannot change mode dynamically without a full unmount/remount of the component. As otherwise clicks inside will also close the popover.
    // e.g. when you want to set to 'manual' when an outside input element is focused, and you want to prevent the popover from closing when clicking outside.
    // In this cases use mode 'manual' and hande outside clicks / focus changes manually.
    /** 
     * @type {HTMLElement} 
     * Reference to the popover for programmatic control:
     * ref.showPopover() or ref.hidePopover()
     * */
    ref = $bindable(),
    /** 
     * @type {'top'|'top-start'|'top-end'|'top-flush'|'bottom'|'bottom-start'|'bottom-end'|'bottom-flush'|'left'|'left-start'|'left-end'|'right'|'right-start'|'right-end'|'center'} 
     * Defines the popover's position relative to its anchor.
     * 
     * Format: `{axis}-{alignment}`
     * - axis: top, bottom, left, right, center
     * - alignment: start, end, flush (axis alone defaults to centered)
     * 
     * The `-flush` alignment matches the popover dimension to the anchor:
     * - `top-flush`/`bottom-flush`: Popover width matches anchor width (perfect for dropdowns)
     * 
     * Use `-flush` when:
     * - Creating dropdowns/select menus that extend from a button
     * - Maintaining visual alignment between trigger and popover
     * - The popover should feel like an extension of the anchor element
     * 
     * @example
     * placement="bottom-flush"  // Dropdown list matching button width
     * placement="bottom-start"  // Standard dropdown, left-aligned
     * placement="right"  // Tooltip to the right, vertically centered
     */
    placement = "bottom-start",
    /** @type {HTMLElement} Element to which the popover is anchored */
    anchor,
    /** @type {boolean} flip checks if space is sufficient for displaying the popover, if not it will flip the position first on the main axis and then the cross axis  */
		flip = true,
    /** @type {number} Intent bias multiplier (default: 1.25). Higher values make the popover prefer the original placement more strongly. Set to 1.0 to disable intent bias. */
    intent_bias = 1.25,
    /** @type {boolean} @readonly the open/ closed state of the popover */
		is_open = $bindable(),
    /** @type {(is_open: boolean) => void} Callback function triggered when popover visibility changes */
    on_toggle,
    /** @type {boolean} Toggles the display of a backdrop behind the popover */
    backdrop = false,
    /** @type {'normal'|'wide'|'ghostly'|string} variants + additional CSS classes for styling the popover */
    class: css_class,
    /** @type {number} Offset in pixels from the anchor element */
    offset = 0,
    /** @type {string} Additional CSS styles for the popover */
    style: css_style,
    /** 
     * @type {number} Expected content height for flip logic. 
     * Tells the positioning algorithm how much vertical space your content needs.
     * If available space is less than this, the popover will flip to a better placement.
     * Set this to match your content's actual minimum height requirement.
     * Defaults to 132px (3 × touchable target size, suitable for short dropdown lists).
     */
    expected_height = 132,
    /** 
     * @type {number} Expected content width for flip logic.
     * Tells the positioning algorithm how much horizontal space your content needs.
     * If available space is less than this, the popover will flip to a better placement.
     * Set this to match your content's actual minimum width requirement.
     * Defaults to 200px (suitable for typical dropdown menus).
     */
    expected_width = 200,
	} = $props();

  // svelte-ignore state_referenced_locally (synced via $effect below)
  let current_placement = $state(placement); // Track current placement for dynamic repositioning
  
  // Sync with prop changes
  $effect(() => {
    current_placement = placement;
  });

  let class_names = $derived(unique_classes(
    'popover',
    'shadow-sm', 
    css_class,
    `place-${current_placement}` // Added dynamic placement class
  ));

  // Constants for positioning calculations
  const MARGIN = 20;
  const MIN_MARGIN = 4;
  const COMFORTABLE_VIEWPORT = 600;
  const SUB_MINIMUM_PENALTY = 0.1;

  /** Scales margin down for constrained viewports (below 600px). */
  function get_adaptive_margin(dimension) {
    if (dimension >= COMFORTABLE_VIEWPORT) return MARGIN;
    return Math.max(MIN_MARGIN, (dimension / COMFORTABLE_VIEWPORT) * MARGIN);
  }

  /** Returns viewport dimensions, preferring visualViewport for mobile accuracy. */
  function get_viewport() {
    const vp = window.visualViewport;
    return { width: vp?.width ?? window.innerWidth, height: vp?.height ?? window.innerHeight };
  }

  /**
   * Returns the page scroll offset (CSS px) used for viewport→page coordinate conversion.
   *
   * Notes:
   * - `getBoundingClientRect()` returns viewport-relative coordinates.
   * - With `position: absolute`, the containing block is not the same as with `position: fixed`.
   *   (Top-layer + popover/native vs polyfill details vary by environment.)
   * - This component computes x/y in viewport coordinates, then may need to add page offsets
   *   when applying translate3d(), depending on how the environment treats `[popover]`.
   *
   * We handle this by *probing at runtime* and locking in the required mode
   * (see `absolute_offset_mode`).
   */
  function get_page_offset() {
    const vp = window.visualViewport;
    const x = vp?.pageLeft ?? window.scrollX ?? document.documentElement.scrollLeft ?? 0;
    const y = vp?.pageTop ?? window.scrollY ?? document.documentElement.scrollTop ?? 0;
    return { x, y };
  }

  // Determines how to interpret translate3d() coordinates when using CSS `position: absolute`.
  // - 'auto': detect at runtime (recommended)
  // - 'viewport': treat translate as viewport coords (do NOT add scroll offsets)
  // - 'page': treat translate as page coords (add scroll offsets)
  //
  // Why detection is needed:
  // We position using viewport math (getBoundingClientRect). With CSS `position: absolute`,
  // the final translate3d() sometimes needs page offsets to remain anchored on scroll.
  // Rather than assuming a single model (native popover vs polyfill, engine quirks),
  // we probe once at runtime and lock in whether scroll offsets are required.
  let absolute_offset_mode = $state('auto');

  let resize_observer;
  let mutation_observer;
  
  // Anti-flicker state: tracks placement to prevent rapid oscillation
  let last_determined_placement = $state(null);
  let placement_lock_until = $state(0);

  // TODO: implement placements depending on viewport:
	// formulas: screen-center, screen-top, screen-top-right, screen-right, screen-bottom-right, screen-bottom, screen-bottom-left, screen-left, screen-top-left
  // TODO: replace this with CSS Anchor Positioning once browser support is better
  const placement_formulas = {
    // 'placement-name': (anchor_size_and_position, popover_width, popover_height) => ({ x: formula, y: formula })
    'top':            (a, width, height) => ({ x: a.x + a.width / 2 - width / 2, y: a.y - height - offset }),
    'top-start':      (a, width, height) => ({ x: a.x, y: a.y - height - offset }),
    'top-end':        (a, width, height) => ({ x: a.x + a.width - width, y: a.y - height - offset }),
    'top-flush':        (a, width, height) => ({ x: a.x, y: a.y - height - offset, width: a.width }),
    'bottom':         (a, width, height) => ({ x: a.x + a.width / 2 - width / 2, y: a.y + a.height + offset }),
    'bottom-start':   (a, width, height) => ({ x: a.x, y: a.y + a.height + offset }),
    'bottom-end':     (a, width, height) => ({ x: a.x + a.width - width, y: a.y + a.height + offset }),
    'bottom-flush':     (a, width, height) => ({ x: a.x, y: a.y + a.height + offset, width: a.width }),
    'left':           (a, width, height) => ({ x: a.x - width - offset, y: a.y + a.height / 2 - height / 2 }),
    'left-start':     (a, width, height) => ({ x: a.x - width - offset, y: a.y }),
    'left-end':       (a, width, height) => ({ x: a.x - width - offset, y: a.y + a.height - height }),
    'right':          (a, width, height) => ({ x: a.x + a.width + offset, y: a.y + a.height / 2 - height / 2 }),
    'right-start':    (a, width, height) => ({ x: a.x + a.width + offset, y: a.y }),
    'right-end':      (a, width, height) => ({ x: a.x + a.width + offset, y: a.y + a.height - height }),
    'center':         (a, width, height) => ({ x: a.x + a.width / 2 - width / 2, y: a.y + a.height / 2 - height / 2 }),
  };

  const OPPOSITE_AXIS = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
  const VERTICAL_AXES = new Set(['top', 'bottom']);

  function get_direction_parts(placement_string) {
    const [axis, align] = placement_string.split('-');
    return { axis, align };
  }

  function is_vertical_axis(axis) {
    return VERTICAL_AXES.has(axis);
  }

  function calculate_available_space(anchor_rect, viewport) {
    return {
      top: anchor_rect.top,
      bottom: viewport.height - anchor_rect.bottom,
      left: anchor_rect.left,
      right: viewport.width - anchor_rect.right
    };
  }

  /**
   * Calculates quality score based on usable surface area (width × height the popover can use).
   * Scores below expected_width/expected_height thresholds are heavily penalized.
   */
  function calculate_placement_quality(
    placement_option, 
    is_original_intent, 
    popover_width,
    popover_height,
    anchor_rect,
    viewport,
    v_margin,
    h_margin
  ) {
    if (expected_width <= 0 || expected_height <= 0) {
      console.warn('Popover: Invalid expected_width or expected_height');
      return is_original_intent ? intent_bias : 1.0;
    }

    const space = calculate_available_space(anchor_rect, viewport);
    const { axis, align } = get_direction_parts(placement_option);
    const is_vertical = is_vertical_axis(axis);
    
    let usable_width, usable_height;
    
    if (is_vertical) {
      const available_height = space[axis] - offset - v_margin;
      usable_height = Math.min(popover_height, Math.max(0, available_height));
      usable_width = align === 'flush' 
        ? anchor_rect.width 
        : Math.min(popover_width, viewport.width - 2 * h_margin);
    } else if (axis === 'left' || axis === 'right') {
      const available_width = space[axis] - offset - h_margin;
      usable_width = Math.min(popover_width, Math.max(0, available_width));
      usable_height = align === 'flush' 
        ? anchor_rect.height 
        : Math.min(popover_height, viewport.height - 2 * v_margin);
    } else { // 'center'
      usable_width = Math.min(popover_width, viewport.width - 2 * h_margin);
      usable_height = Math.min(popover_height, viewport.height - 2 * v_margin);
    }
    
    const usable_area = usable_width * usable_height;
    const intent_multiplier = is_original_intent ? intent_bias : 1.0;
    
    // Heavily penalize placements below minimum requirements
    if (usable_width < expected_width || usable_height < expected_height) {
      const penalty = (usable_width / expected_width) * (usable_height / expected_height) * SUB_MINIMUM_PENALTY;
      return penalty * intent_multiplier;
    }
    
    return (usable_area / (expected_width * expected_height)) * intent_multiplier;
  }

/** Finds best alignment (start/center/end/flush) for axis, prioritizing original. */
function adjust_alignment(axis, popover_width, popover_height, anchor_rect, viewport, v_margin, h_margin) {
  const { align: original_align, axis: original_axis } = get_direction_parts(placement);
  const is_vertical = is_vertical_axis(axis);
  const original_is_vertical = is_vertical_axis(original_axis);

  // Preserve 'flush' only when staying on same axis orientation
  if (original_align === 'flush' && is_vertical === original_is_vertical) {
    return 'flush';
  }

  function check_flush(align) {
    if (is_vertical) {
      const width = align === 'flush' ? anchor_rect.width : popover_width;
      let left;
      if (align === 'start' || align === 'flush') left = anchor_rect.left;
      else if (align === 'center') left = anchor_rect.left + anchor_rect.width / 2 - width / 2;
      else if (align === 'end') left = anchor_rect.right - width;
      else return false;
      return left >= h_margin && left + width <= viewport.width - h_margin;
    } else {
      const height = align === 'flush' ? anchor_rect.height : popover_height;
      let top;
      if (align === 'start' || align === 'flush') top = anchor_rect.top;
      else if (align === 'center') top = anchor_rect.top + anchor_rect.height / 2 - height / 2;
      else if (align === 'end') top = anchor_rect.bottom - height;
      else return false;
      return top >= v_margin && top + height <= viewport.height - v_margin;
    }
  }

  // Try original alignment first, then alternatives
  const try_align = original_align === 'flush' ? null : original_align;
  if (try_align && check_flush(try_align)) return try_align;
  
  for (const align of ['start', 'center', 'end']) {
    if (align !== try_align && check_flush(align)) return align;
  }

  return try_align || 'start';
}

/**
 * Finds optimal placement using quality scoring with anti-flicker protection.
 * Uses stability bias (15%), flip threshold (10%), and cooldown (300ms).
 */
function determine_optimal_placement(popover_width, popover_height, anchor_rect, viewport, v_margin, h_margin, force = false) {
  const COOLDOWN_MS = 300;
  const STABILITY_BIAS = 1.15;
  const FLIP_THRESHOLD = 1.10;
  
  const { axis: original_axis } = get_direction_parts(placement);
  const now = Date.now();
   
  // Respect cooldown unless forced (e.g., window resize)
  if (!force && last_determined_placement && now < placement_lock_until) {
    return last_determined_placement;
  }
   
  // Build candidates: original, opposite, then cross axes
  const is_vertical = is_vertical_axis(original_axis);
  const candidates = [
    { axis: original_axis, is_original: true },
    { axis: OPPOSITE_AXIS[original_axis], is_original: false },
    ...(is_vertical 
      ? [{ axis: 'right', is_original: false }, { axis: 'left', is_original: false }]
      : [{ axis: 'bottom', is_original: false }, { axis: 'top', is_original: false }])
  ];
   
  let best_placement = null;
  let best_score = -1;
  let current_score = -1;
   
  for (const { axis, is_original } of candidates) {
    const align = adjust_alignment(axis, popover_width, popover_height, anchor_rect, viewport, v_margin, h_margin);
    const test_placement = `${axis}-${align}`;
    
    let score = calculate_placement_quality(
      test_placement, is_original, popover_width, popover_height,
      anchor_rect, viewport, v_margin, h_margin
    );
    
    // Current placement gets stability bonus
    if (test_placement === last_determined_placement) {
      score *= STABILITY_BIAS;
      current_score = score;
    }
     
    if (score > best_score) {
      best_score = score;
      best_placement = test_placement;
    }
  }
   
  // Only flip if improvement exceeds threshold
  if (last_determined_placement && current_score > 0 && 
      best_placement !== last_determined_placement && 
      best_score < current_score * FLIP_THRESHOLD) {
    return last_determined_placement;
  }
   
  if (best_placement !== last_determined_placement) {
    placement_lock_until = now + COOLDOWN_MS;
  }
   
  last_determined_placement = best_placement || `${original_axis}-start`;
  return last_determined_placement;
}

  /** Checks if placement has insufficient space based on expected_height/expected_width requirements. */
  function is_placement_overflowing(anchor_rect, test_placement, viewport, v_margin, h_margin) {
    const { axis } = get_direction_parts(test_placement);
    
    if (axis === 'center') return false;
    
    if (is_vertical_axis(axis)) {
      const space = axis === 'bottom' 
        ? viewport.height - anchor_rect.bottom - v_margin
        : anchor_rect.top - v_margin;
      return space < expected_height + offset;
    }
    
    // Horizontal axis (left/right)
    const space = axis === 'right'
      ? viewport.width - anchor_rect.right - h_margin
      : anchor_rect.left - h_margin;
    return space < expected_width + offset;
  }

  /** 
   * Checks if element is actually visible - both in viewport AND not occluded by sticky/fixed elements.
   * Uses elementFromPoint for efficient occlusion detection (handles sticky navs, fixed headers, etc).
   * @param {DOMRect} rect - The element's bounding client rect
   * @param {HTMLElement} element - The element to check visibility for
   */
  function is_element_visible(rect, element) {
    const vp = window.visualViewport;
    const offset_top = vp?.offsetTop ?? 0;
    const offset_left = vp?.offsetLeft ?? 0;
    const height = vp?.height ?? window.innerHeight;
    const width = vp?.width ?? window.innerWidth;
    
    // First check: is the element at least partially in the viewport?
    const in_viewport = rect.bottom > offset_top && rect.top < offset_top + height &&
                        rect.right > offset_left && rect.left < offset_left + width;
    
    if (!in_viewport) return false;
    
    // Second check: is the center of the visible portion actually visible (not occluded)?
    // Calculate the center of the visible portion within viewport bounds
    const visible_top = Math.max(rect.top, offset_top);
    const visible_bottom = Math.min(rect.bottom, offset_top + height);
    const visible_left = Math.max(rect.left, offset_left);
    const visible_right = Math.min(rect.right, offset_left + width);
    
    const center_x = (visible_left + visible_right) / 2;
    const center_y = (visible_top + visible_bottom) / 2;
    
    // Check what element is at that point
    const element_at_point = document.elementFromPoint(center_x, center_y);
    
    // If no element found, consider not visible
    if (!element_at_point) return false;
    
    // Element is visible if point hits the element itself, any of its descendants,
    // OR if the popover itself is covering it (which is expected behavior)
    return element === element_at_point || 
           element.contains(element_at_point) || 
           ref?.contains(element_at_point);
  }

  /** 
   * Computes the position of the popover based on the anchor's position and popover dimensions.
   * @param {boolean} force_recalculate - If true, bypass placement cooldown for immediate recalculation
   */
  function compute_position(force_recalculate = false) {
    if (!browser || !ref || !is_open || is_closing) return;

    const viewport = get_viewport();
    const vertical_margin = get_adaptive_margin(viewport.height);
    const horizontal_margin = get_adaptive_margin(viewport.width);
    let styles = {};
    let final_placement = placement;
    current_placement = final_placement;

    // Center in viewport if no anchor
    if (!anchor) {
      const rect = ref.getBoundingClientRect();
      const x = parseInt((viewport.width - rect.width) / 2);
      const y = parseInt((viewport.height - rect.height) / 2);
      ref.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      return;
    }

    // Hide if anchor scrolled off-screen or occluded by sticky/fixed elements
    // Skip this check when virtual keyboard is visible — the keyboard pushes the anchor
    // out of the visual viewport but the popover should remain open.
    const anchor_rect_viewport = anchor.getBoundingClientRect();
    if (!is_keyboard_visible && !is_element_visible(anchor_rect_viewport, anchor)) {
      ref.hidePopover();
      return;
    }
    
    const popover_rect = ref.getBoundingClientRect();
    const popover_width = popover_rect.width;
    const popover_height = popover_rect.height;

    // Check overflow and find optimal placement if needed
    if (flip && is_placement_overflowing(anchor_rect_viewport, final_placement, viewport, vertical_margin, horizontal_margin)) {
      final_placement = determine_optimal_placement(popover_width, popover_height, anchor_rect_viewport, viewport, vertical_margin, horizontal_margin, force_recalculate);
      current_placement = final_placement;
    }

    // Set max constraints based on placement axis
    const { axis: placement_axis, align: placement_align } = get_direction_parts(final_placement);
    const is_flush = placement_align === 'flush';
    const is_vertical = is_vertical_axis(placement_axis);
    
    if (is_vertical) {
      const max_h = placement_axis === 'bottom'
        ? viewport.height - anchor_rect_viewport.bottom - offset - vertical_margin
        : anchor_rect_viewport.top - offset - vertical_margin;
      styles.maxHeight = `${Math.max(0, max_h)}px`;
      if (!is_flush) styles.maxWidth = `${Math.max(0, viewport.width - 2 * horizontal_margin)}px`;
    } else if (placement_axis === 'left' || placement_axis === 'right') {
      const max_w = placement_axis === 'right'
        ? viewport.width - anchor_rect_viewport.right - offset - horizontal_margin
        : anchor_rect_viewport.left - offset - horizontal_margin;
      styles.maxWidth = `${Math.max(0, max_w)}px`;
      styles.maxHeight = `${Math.max(0, viewport.height - 2 * vertical_margin)}px`;
    }

    // For -flush placements, force popover width to match anchor width
    if (is_flush) {
      const flush_width = `${Math.max(0, anchor_rect_viewport.width)}px`;
      styles.width = flush_width;
      styles.minWidth = flush_width;
      styles.maxWidth = flush_width;
    }

    // Calculate position and clamp to viewport (cross-axis only to maintain anchor relationship)
    const pos = placement_formulas[final_placement](anchor_rect_viewport, popover_width, popover_height);
    let x = pos.x;
    let y = pos.y;
    
    // Clamp in viewport coordinates first (anchor + viewport math),
    // then convert to page coordinates when applying the final transform (see get_page_offset()).
    if (is_vertical) {
      x = Math.max(horizontal_margin, Math.min(x, viewport.width - popover_width - horizontal_margin));
    } else if (placement_axis === 'left' || placement_axis === 'right') {
      y = Math.max(vertical_margin, Math.min(y, viewport.height - popover_height - vertical_margin));
    } else { // 'center'
      x = Math.max(horizontal_margin, Math.min(x, viewport.width - popover_width - horizontal_margin));
      y = Math.max(vertical_margin, Math.min(y, viewport.height - popover_height - vertical_margin));
    }
    
    // Apply other styles first; transform is handled separately so we can auto-detect offset mode
    // without causing visible flicker (the browser will only paint the final state).
    Object.assign(ref.style, styles);

    // Desired position in viewport coordinates (from anchor.getBoundingClientRect()).
    const desired_x_viewport = parseInt(x);
    const desired_y_viewport = parseInt(y);

    const page_offset = get_page_offset();
    const has_page_offset = Math.abs(page_offset.x) > 0 || Math.abs(page_offset.y) > 0;

    // Auto-detect only when there is a non-zero page offset. At scrollX/scrollY=0, both modes
    // behave the same, so "locking in" would be unreliable.
    if (absolute_offset_mode === 'auto' && has_page_offset) {
      // Probe using viewport coordinates to measure how the browser interprets translate3d().
      ref.style.transform = `translate3d(${desired_x_viewport}px, ${desired_y_viewport}px, 0)`;
      const actual_rect = ref.getBoundingClientRect();

      // Measure positioning error: if browser uses page coords, dx/dy will equal page_offset.
      const dx = desired_x_viewport - actual_rect.left;
      const dy = desired_y_viewport - actual_rect.top;

      // Choose mode with smaller positioning error.
      const viewport_error = Math.abs(dx) + Math.abs(dy);
      const page_error = Math.abs(dx - page_offset.x) + Math.abs(dy - page_offset.y);
      absolute_offset_mode = page_error < viewport_error ? 'page' : 'viewport';
    }

    const should_add_page_offset = absolute_offset_mode === 'page';
    const translate_x = desired_x_viewport + (should_add_page_offset ? page_offset.x : 0);
    const translate_y = desired_y_viewport + (should_add_page_offset ? page_offset.y : 0);

    // Store for exit animation
    last_translate_x = translate_x;
    last_translate_y = translate_y;

    // Final transform:
    // - 'viewport' mode: translate3d(viewport_x, viewport_y)
    // - 'page' mode: translate3d(viewport_x + scrollX, viewport_y + scrollY)
    ref.style.transform = `translate3d(${translate_x}px, ${translate_y}px, 0)`;
  }

  let last_viewport_height = $state();
  let is_keyboard_visible = $state(false);
  
  // Exit animation duration in ms - single source of truth, passed to CSS via custom property
  const EXIT_ANIMATION_DURATION = 120;
  let close_timeout_id = null;
  let is_closing = $state(false);
  let last_translate_x = 0;
  let last_translate_y = 0;

  function handle_viewport_change() {
    if (!is_open || is_closing) return;

    const height = window.visualViewport?.height ?? window.innerHeight;

    // Detect keyboard visibility changes (>100px height change)
    // Skip only this frame to avoid jump, but continue tracking on subsequent scrolls
    if (last_viewport_height && Math.abs(last_viewport_height - height) > 100) {
      is_keyboard_visible = height < last_viewport_height;
      last_viewport_height = height;
      return;
    }
    // if (!is_keyboard_visible) {
    //   last_viewport_height = height;
    //   compute_position();
    // }

    // Always update position on scroll
    last_viewport_height = height;
    compute_position();
  }

  $effect(() => {
    if (browser && is_open && anchor) {
      document.addEventListener('scroll', handle_viewport_change, { passive: true, capture: true });
      window.visualViewport?.addEventListener('resize', handle_viewport_change, { passive: true });
      window.visualViewport?.addEventListener('scroll', handle_viewport_change, { passive: true });

      return () => {
        document.removeEventListener('scroll', handle_viewport_change, true);
        window.visualViewport?.removeEventListener('resize', handle_viewport_change);
        window.visualViewport?.removeEventListener('scroll', handle_viewport_change);
      };
    }
  });

  /**
   * Handles clicks that should close the popover with animation.
   * Uses click event (not pointerdown) to properly intercept popovertarget behavior.
   */
  function handle_close_click(event) {
    if (!is_open || is_closing) return;
    
    const is_inside_popover = ref?.contains(event.target);
    const is_inside_anchor = anchor?.contains(event.target);
    
    // Check if clicking a trigger button for this popover
    const trigger = event.target.closest(`[popovertarget="${id}"]`);
    const is_trigger_click = trigger && !is_inside_popover;
    
    // Light dismiss: click outside popover and anchor (auto mode only)
    const is_light_dismiss = mode === 'auto' && !is_inside_popover && !is_inside_anchor && !is_trigger_click;
    
    if (is_trigger_click || is_light_dismiss) {
      event.preventDefault();
      event.stopPropagation();
      start_close_animation();
    }
  }

  // ESC key handler for animated close (auto mode only)
  function handle_escape(event) {
    if (!is_open || is_closing || mode !== 'auto') return;
    
    if (event.key === 'Escape') {
      event.preventDefault();
      start_close_animation();
    }
  }

  // Register close intent handlers when popover is open
  $effect(() => {
    if (browser && is_open) {
      // Use capture to intercept before native popovertarget behavior
      document.addEventListener('click', handle_close_click, { capture: true });
      if (mode === 'auto') {
        document.addEventListener('keydown', handle_escape, { capture: true });
      }

      return () => {
        document.removeEventListener('click', handle_close_click, true);
        document.removeEventListener('keydown', handle_escape, true);
      };
    }
  });

  function handle_resize() {
    if (is_open && !is_closing) compute_position(true);
  }

  /** Resets placement tracking state (used when opening/closing). */
  function reset_placement_state() {
    last_determined_placement = null;
    placement_lock_until = 0;
  }

  /** Finalizes close by resetting all state and notifying listeners. */
  function finalize_close() {
    is_closing = false;
    is_open = false;
    cleanup_observers();
    reset_placement_state();
    on_toggle?.(false);
  }

  /**
   * Initiates animated close sequence.
   * Call this instead of ref.hidePopover() directly.
   */
  function start_close_animation() {
    if (!ref || !is_open || is_closing) return;
    
    is_closing = true;
    
    // Apply exit animation: scale toward anchor + fade
    // The .popover-closing class enables the CSS transition
    ref.style.transform = `translate3d(${last_translate_x}px, ${last_translate_y}px, 0) scale(0.96)`;
    ref.style.opacity = '0';
    
    // After animation completes, actually hide
    close_timeout_id = setTimeout(() => {
      close_timeout_id = null;
      
      if (ref) {
        // Hide first, then reset styles (prevents flicker)
        ref.hidePopover();
        ref.style.opacity = '';
        ref.style.transform = '';
      }
      
      finalize_close();
    }, EXIT_ANIMATION_DURATION);
  }

  /**
   * Cancels exit animation and keeps popover open.
   */
  function cancel_close_animation() {
    if (!close_timeout_id) return;
    
    clearTimeout(close_timeout_id);
    close_timeout_id = null;
    is_closing = false;
    
    // Reset to open state - remove inline styles to revert to CSS defaults
    if (ref) {
      ref.style.opacity = '';
      ref.style.transform = `translate3d(${last_translate_x}px, ${last_translate_y}px, 0)`;
    }
  }

  function handle_toggle(event) {
    const is_opening = event.newState === 'open';

    if (is_opening) {
      // Cancel any pending close - enables instant re-open during exit animation
      cancel_close_animation();
      
      is_open = true;
      on_toggle?.(true);
      
      reset_placement_state();
      init_observers();
      last_viewport_height = window.visualViewport?.height ?? window.innerHeight;
      is_keyboard_visible = false;

      if (ref) ref.style.visibility = 'hidden';

      // Delay for Safari layout calculation
      setTimeout(() => {
        if (!is_open || !ref) return;
        compute_position();
        requestAnimationFrame(() => {
          if (is_open && ref) ref.style.visibility = 'visible';
        });
      }, 10);
    } else {
      // Native close happened (ESC key, light dismiss in auto mode)
      // For auto mode: the popover already closed, just update state
      // For manual mode: this shouldn't fire unless we called hidePopover()
      finalize_close();
    }
  }

  function init_observers() {
    if (!mutation_observer && ref) {
      mutation_observer = new MutationObserver((mutations) => {
        // Reposition when content changes (nodes added OR removed)
        if (mutations.some(m => m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0))) {
          compute_position();
        }
      });
      mutation_observer.observe(ref, { childList: true, subtree: true });
    }

    // Observe anchor resize (not popover - would cause feedback loop)
    if (!resize_observer && anchor) {
      resize_observer = new ResizeObserver(() => is_open && compute_position());
      resize_observer.observe(anchor);
    }
  }

  function cleanup_observers() {
    mutation_observer?.disconnect();
    mutation_observer = null;
    resize_observer?.disconnect();
    resize_observer = null;
  }

  // Portal to document.body to avoid clipping/stacking context issues from parent elements
  let original_parent_element;
  
  // CSS variables used by this component - captured from parent before portaling
  const INHERITED_CSS_VARS = [
    '--color-background', '--color-foreground', 
    '--color-default-background', '--color-default-foreground',
    '--color-emphasis-background', '--color-emphasis-foreground',
    '--color-default-emphasis-background', '--color-default-emphasis-foreground',
    '--color-background-lux-mode', '--color-foreground-lux-mode',
    '--color-default-background-lux-mode', '--color-default-foreground-lux-mode',
    '--color-emphasis-background-lux-mode', '--color-emphasis-foreground-lux-mode',
    '--text-1', '--text-2', '--text-3',
    '--color-default-stroke', '--popover-radius', '--popover-text-color',
    '--transluscent-filter', '--touchable-target-min-size', '--border-opacity',
    '--icon-color1', '--backdrop-background', '--backdrop-opacity',
    '--font-smoothing'
  ];
  
  /** Copies CSS custom properties from original parent to preserve theming after portal. */
  function capture_and_apply_inherited_styles() {
    if (!ref || !original_parent_element) return;
    const parent_styles = window.getComputedStyle(original_parent_element);
    for (const prop of INHERITED_CSS_VARS) {
      const value = parent_styles.getPropertyValue(prop);
      if (value) ref.style.setProperty(prop, value);
    }
  }
  
  // Runs synchronously before the popover shows, so the first frame already has the theme.
  // Not on mount: getComputedStyle there forced a style recalc per instance, and a list
  // with hundreds of popovers (the messenger's contacts) hung the page for 30 s.
  function handle_beforetoggle(event) {
    if (event.newState === 'open') capture_and_apply_inherited_styles();
  }

  onMount(() => {
    if (ref && ref.parentNode !== document.body) {
      // Store reference to original parent for CSS inheritance (captured on open)
      original_parent_element = ref.parentElement;

      // Move to body
      document.body.appendChild(ref);
    }
  });

  onDestroy(() => {
    // Cancel any pending close timeout
    if (close_timeout_id) {
      clearTimeout(close_timeout_id);
      close_timeout_id = null;
    }
    cleanup_observers();
    // Clean up the inserted popover from document.body when the component is destroyed.
    if (ref && ref.parentNode === document.body) {
      document.body.removeChild(ref);
    }
  });
</script>

<svelte:window onresize={handle_resize} />

<div
  bind:this={ref}
  id={id}
  popover="manual"
  role="dialog"
  onbeforetoggle={handle_beforetoggle}
  ontoggle={handle_toggle}
  class:backdrop={backdrop}
  class:popover-closing={is_closing}
  class={class_names}
  style="--exit-duration: {EXIT_ANIMATION_DURATION}ms; {css_style ?? ''}"
>
  <div class="popover-content">
    {#if is_open && children}
      {@render children()}
    {/if}
  </div>
</div>

<style lang="postcss">
  :global([popover]) {
    will-change: transform;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    box-sizing: border-box;
    /* Top-layer positioning:
       - In the top layer, `position: fixed` is viewport-relative.
       - In the top layer, `position: absolute` is relative to the initial containing block (page/document).
       - Our positioning math is based on `getBoundingClientRect()` (viewport coordinates).
       - Therefore, when using `position: absolute` we MUST convert viewport → page coordinates by adding
         scroll offsets (implemented in `compute_position()` via `get_page_offset()`).
       - If we instead switch to `position: fixed`, we must REMOVE the page-offset conversion and keep
         translate3d(...) in pure viewport coordinates.
    */
    position: absolute;
    left: 0;
    top: 0;
    width: fit-content;
    height: fit-content;
    /* NO unconditional display: a CLOSED popover must not create layout —
       it kept its last width/position as an absolute element and caused
       phantom horizontal overflow on narrower viewports. Display is granted
       by the open/closing states below (UA default for closed is none). */
    flex-direction: column;
		border: none;
    outline: none;
    margin: 0;
    padding: 0;
    max-width: 100vw;
    max-height: 100vh;
    /* Display at least button size items or the actual height in case of less items */
    min-height: min(calc(4 * var(--touchable-target-min-size)), fit-content);
    overflow: hidden;
    visibility: hidden;
    background-color: var(--color-background, var(--color-default-background)); /** This works now because the css inheritance is preserved by manual capture of the parent's css variables in javascript */
    backdrop-filter: var(--transluscent-filter);
    border-radius: var(--popover-radius, var(--radius-xs));
    border: 1px solid var(--color-default-stroke);
    pointer-events: none; /* Ensure non-open popovers can't be interacted with */
    &.ghostly {
      background-color: transparent;
      border: none;
      backdrop-filter: none;
    }
    > .popover-content {
      /* padding: var(--size-2); */
      --icon-color1:  var(--popover-text-color, var(--color-default-foreground));
      --border-opacity: 0.14; /* equivalent to --color-default-stroke-100 on white background*/
      scrollbar-width: thin;
      scrollbar-color: var(--color-default-foreground-800) var(--color-default-background);
      overflow-y: auto;
      overflow-x: hidden;
      /* height: 100%; */
      height: auto;
      max-height: 100vh;
      color: var(--popover-text-color, var(--color-default-foreground));  
      :global( > *){
        --border-radius: var(--popover-radius, var(--radius-xs));
      }
    }    
  }
	:global([popover].backdrop::backdrop) {
    background-color: var(--backdrop-background, #000);
    opacity: 0;
    /* TODO: prevent users from clicking elements below the backdrop */
  }

  /* Base popover state */
  :global([popover]), :global(::backdrop) {
    opacity: 0;
  }

  /* Open state - instant (no entry animation) */
  :global(:popover-open),
  :global(:popover-open::backdrop),
  :global([open]) {
    opacity: 1;
    pointer-events: auto;
  }
  :global([popover]:popover-open::backdrop) {
    opacity: var(--backdrop-opacity, 0.3);
  }

  :global([popover]:popover-open) {
    visibility: visible;
    display: flex;
	}

  :global([popover].popover-open) {
    visibility: visible;
    opacity: 1;
    display: flex;
  }

  /* Keep the element rendered while the exit animation plays */
  :global([popover].popover-closing) {
    display: flex;
  }

  /* Exit animation - only enabled via .popover-closing class */
  .popover {
    /* NO default transition - prevents fade-in on open */
    
    /* Transform-origin per placement - scale toward anchor direction */
    /* Bottom placements - anchor is above, scale toward top */
    &.place-bottom { transform-origin: center top; }
    &.place-bottom-start { transform-origin: left top; }
    &.place-bottom-end { transform-origin: right top; }
    &.place-bottom-flush { transform-origin: center top; }
    
    /* Top placements - anchor is below, scale toward bottom */
    &.place-top { transform-origin: center bottom; }
    &.place-top-start { transform-origin: left bottom; }
    &.place-top-end { transform-origin: right bottom; }
    &.place-top-flush { transform-origin: center bottom; }
    
    /* Left placements - anchor is to the right, scale toward right */
    &.place-left { transform-origin: right center; }
    &.place-left-start { transform-origin: right top; }
    &.place-left-end { transform-origin: right bottom; }
    
    /* Right placements - anchor is to the left, scale toward left */
    &.place-right { transform-origin: left center; }
    &.place-right-start { transform-origin: left top; }
    &.place-right-end { transform-origin: left bottom; }
    
    /* Center placement */
    &.place-center { transform-origin: center center; }
    
    /* Exit animation - enabled only when closing */
    &.popover-closing {
      transition: transform var(--exit-duration) ease-out, opacity var(--exit-duration) ease-out;
    }
  }

</style>